import { Box, Button, Text, useBreakpointValue } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Loading from 'components/loading'
import NotificationBadge from 'components/notification-badge'

import theme from 'theme'

import type { ButtonProps } from '@chakra-ui/react'
import type { ButtonPrimaryIconProps } from 'components/button-primary'

const buttonCoreStyle = {
  regress: {
    background: 'transparent',
    border: `solid 1px ${theme.baseColors.primary.main}`,
  },
  progress: {
    background: theme.baseColors.primary.main,
    border: `solid 1px ${theme.baseColors.primary.main}`,
  },
  transparent: {
    background: 'transparent',
    border: 'none',
  },
  destructive: {
    background: theme.baseColors.danger.main,
    border: `solid 1px ${theme.baseColors.danger.main}`,
  },
}

const buttonHoverStyle = {
  regress: {
    background: theme.colors.alpha.whitealpha['100'],
    border: `solid 1px ${theme.baseColors.primary.main}`,
  },
  progress: {
    background: theme.colors.blue['500'],
    border: `solid 1px ${theme.colors.blue['500']}`,
  },
  transparent: {
    background: theme.colors.alpha.blackalpha['200'],
    border: 'none',
  },
  destructive: {
    background: theme.baseColors.danger.dark,
    border: `solid 1px ${theme.baseColors.danger.dark}`,
  },
}

const buttonActiveStyle = {
  regress: {
    background: theme.colors.alpha.whitealpha['200'],
    border: `solid 1px ${theme.baseColors.primary.main}`,
  },
  progress: {
    background: theme.baseColors.primary.dark,
    border: `solid 1px ${theme.baseColors.primary.dark}`,
  },
  transparent: {
    background: 'transparent',
    border: `solid 1px ${theme.baseColors.primary.main}`,
  },
  destructive: {
    background: theme.colors.red['700'],
    border: `solid 1px ${theme.colors.red['700']}`,
  },
}

export type ButtonNavigationProps = {
  text: string
  type: 'regress' | 'progress' | 'transparent' | 'destructive'
  icon?: ButtonPrimaryIconProps
  isDisabled?: boolean
  isHidden?: boolean
  isLoading?: boolean
  hasNotification?: boolean
  onClick?: () => void
} & Omit<ButtonProps, 'children' | 'type'>

const ButtonNavigation = ({
  text,
  type,
  icon,
  isDisabled,
  isHidden,
  isLoading,
  hasNotification,
  onClick,
  ...props
}: ButtonNavigationProps) => {
  const isSmallViewport = useBreakpointValue({ base: true, lg: false })
  const fgColor = props.color ?? theme.colors.white

  return (
    <Button
      h="48px"
      w={(props.w ?? props.width ?? isSmallViewport) ? '100%' : '190px'}
      px="14px"
      borderRadius={theme.radii.base}
      _hover={buttonHoverStyle[type]}
      _active={!isDisabled ? buttonActiveStyle[type] : {}}
      isDisabled={isDisabled}
      visibility={isHidden ? 'hidden' : 'visible'}
      onClick={onClick}
      {...buttonCoreStyle[type]}
      {...props}
    >
      {isLoading ? (
        <Loading size="sm" color={fgColor} />
      ) : (
        <>
          {hasNotification && (
            <Box mr="10px">
              <NotificationBadge />
            </Box>
          )}
          {icon && (
            <Box mr="10px">
              <FontAwesomeIcon icon={icon.source} size="sm" color={fgColor} />
            </Box>
          )}
          <Text textStyle="button1" color={fgColor}>
            {text}
          </Text>
        </>
      )}
    </Button>
  )
}

export default ButtonNavigation
